import EventProcessorWorker from '../eventProcessor.worker'; // eslint-disable-line import/default
import PerformanceUtils from '../../components/utils/PerformanceUtils';

export default class EventProcessorService {
  constructor(Utils, sdk, $q) {
    'ngInject';

    Object.assign(this, { Utils, sdk, $q });
    this.eventProcessor = new EventProcessorWorker();
  }

  init() {
    if (this._initialized) {
      throw 'eventProcessor already initialized';
    }

    this._initialized = true;
    this.eventProcessor.addEventListener(
      'message',
      (e) => {
        if (!__TEST__) {
          console.info(`Message from worker: ${e.data.byteLength / 1024} kB`);
        }

        PerformanceUtils.start('WORKER_MESSAGE_TO_OBJECT');
        const result = JSON.parse(this.Utils.ab2str(e.data));
        PerformanceUtils.end('WORKER_MESSAGE_TO_OBJECT');

        if (result.type === 'events_updated') {
          this?.resolveEventsUpdated();
        } else {
          this.resolve?.(result);
          PerformanceUtils.end('EVENT_PROCESSOR_WITH_WORKER');
        }
      },
      false
    );

    this.eventProcessor.addEventListener('error', (err) => this.reject?.(err));
  }

  processEvents(payload) {
    PerformanceUtils.start('EVENT_PROCESSOR_WITH_WORKER');
    PerformanceUtils.start('PREPARE_WORKER_MESSAGE_PAYLOAD');
    const workerMessage = this.Utils.str2ab(JSON.stringify({ type: 'PROCESS_EVENTS', payload }));
    PerformanceUtils.end('PREPARE_WORKER_MESSAGE_PAYLOAD');

    if (!__TEST__) {
      console.info(`Message to worker: ${workerMessage.byteLength / 1024} kB`);
    }

    this.eventProcessor.postMessage('SEND_DATA_TO_WORKER');
    this.eventProcessor.postMessage(workerMessage, [workerMessage]);
  }

  updatePersonas(personaCollection) {
    const payload = {
      orgUsers: personaCollection.allUsers.map((u) =>
        this.Utils.extendWithProps(u, {}, [
          'id',
          'name',
          'type',
          'lastname',
          'positionIds',
          'locationIds',
          'groupIds',
          'wages',
        ])
      ),
      orgLocations: personaCollection.locations
        .concat(personaCollection.archivedLocations)
        .map((location) =>
          this.Utils.extendWithProps(location, {}, ['id', 'name', 'type', 'timezone'])
        ),
      orgPositions: personaCollection.positions
        .concat(personaCollection.archivedPositions)
        .map((position) =>
          this.Utils.extendWithProps(position, {}, ['id', 'name', 'type', 'color', 'wages'])
        ),
      orgGroups: personaCollection.groups
        .concat(personaCollection.archivedGroups)
        .map((group) =>
          this.Utils.extendWithProps(group, {}, ['id', 'name', 'type', 'hiddenOnSchedule'])
        ),
    };

    const workerMessage = this.Utils.str2ab(JSON.stringify({ type: 'UPDATE_PERSONAS', payload }));

    if (!__TEST__) {
      console.info(`Message to worker: ${workerMessage.byteLength / 1024} kB`);
    }

    this.eventProcessor.postMessage(workerMessage, [workerMessage]);
  }

  updateEvents(eventCollection, { clear, changed = [], deleted = [] }) {
    if (!this._initialized) {
      throw 'eventProcessor not initialized!';
    }

    const addOrEdit = [];
    const removeIds = deleted.map((e) => e.id);

    changed.forEach((event) => {
      if (event.status === 'cancelled') {
        removeIds.push(event.id);
      } else {
        addOrEdit.push(event);
      }
    });

    const payload = {
      clear,
      removeIds,
      addOrEdit: this.stripUserDataOffEvents(addOrEdit),
    };

    const workerMessage = this.Utils.str2ab(JSON.stringify({ type: 'UPDATE_EVENTS', payload }));

    if (!__TEST__) {
      console.info(`Message to worker: ${workerMessage.byteLength / 1024} kB`);
    }

    const returnPromise = this.$q((resolve) => {
      this.resolveEventsUpdated = resolve;
    });

    this.eventProcessor.postMessage(workerMessage, [workerMessage]);
    return returnPromise;
  }

  stripUserDataOffEvents(events) {
    return events.map((event) => {
      const stripped = { ...event };
      if (stripped.user) {
        stripped.user = { id: stripped.user.id };
      }

      if (stripped.applications?.length) {
        stripped.applications = stripped.applications.map((a) => ({
          id: a.id,
          user: { id: a.user.id },
        }));
      }

      if (stripped.timesheetProjections?.length) {
        stripped.timesheetProjections = this.stripUserDataOffEvents(stripped.timesheetProjections);
      }

      return stripped;
    });
  }

  abort() {
    this.eventProcessor.postMessage('ABORT_PROCESSORS');
  }
}
